import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X, ChevronRight, ArrowLeft, Camera, Car, Home, Smartphone, Sofa, ShoppingBag, Baby, Dumbbell, PawPrint, Briefcase, Wrench, Check, Star, Zap, MapPin, Sparkles, ImagePlus, DollarSign, Phone, MessageCircle, FileText, Tag, Upload, Trash2 } from 'lucide-react';
import { trackPageView } from '../analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uaeCities = [
  { en: 'Abu Dhabi', ar: 'أبوظبي' },
  { en: 'Ajman', ar: 'عجمان' },
  { en: 'Al Ain', ar: 'العين' },
  { en: 'Dubai', ar: 'دبي' },
  { en: 'Fujairah', ar: 'الفجيرة' },
  { en: 'Ras Al Khaimah', ar: 'رأس الخيمة' },
  { en: 'Sharjah', ar: 'الشارقة' },
  { en: 'Umm Al Quwain', ar: 'أم القيوين' },
];

const categoryIcons = {
  'Motors': Car, 'Property': Home, 'Electronics': Smartphone,
  'Home & Furniture': Sofa, 'Fashion': ShoppingBag, 'Baby & Kids': Baby,
  'Hobbies & Sports': Dumbbell, 'Pets': PawPrint, 'Jobs': Briefcase, 'Services': Wrench,
};

const categoryTranslations = {
  'Property': 'العقارات', 'Motors': 'السيارات', 'Home & Furniture': 'المنزل والأثاث',
  'Electronics': 'الإلكترونيات', 'Baby & Kids': 'الأطفال والرضع', 'Fashion': 'الأزياء',
  'Pets': 'الحيوانات الأليفة', 'Hobbies & Sports': 'الهوايات والرياضة', 'Services': 'الخدمات',
  'Jobs': 'الوظائف', 'Classifieds': 'إعلانات متنوعة', 'Community': 'المجتمع',
  'Cars for Sale': 'سيارات للبيع', 'Motorcycles': 'دراجات نارية', 'Boats & Jet Skis': 'قوارب وجت سكي',
  'Car Parts & Accessories': 'قطع غيار وإكسسوارات', 'Number Plates': 'لوحات أرقام', 'Heavy Vehicles': 'مركبات ثقيلة',
  'Apartments for Rent': 'شقق للإيجار', 'Apartments for Sale': 'شقق للبيع', 'Villas for Rent': 'فلل للإيجار',
  'Villas for Sale': 'فلل للبيع', 'Commercial': 'تجاري', 'Commercial for Rent': 'تجاري للإيجار',
  'Commercial for Sale': 'تجاري للبيع', 'Land': 'أراضي', 'Rooms for Rent': 'غرف للإيجار',
  'Mobile Phones': 'هواتف محمولة', 'Tablets': 'أجهزة لوحية', 'Laptops': 'لابتوب', 'Computers': 'كمبيوترات',
  'TVs & Audio': 'تلفزيونات وصوتيات', 'Cameras': 'كاميرات', 'Gaming': 'ألعاب إلكترونية',
  'Furniture': 'أثاث', 'Home Appliances': 'أجهزة منزلية', 'Kitchen': 'مطبخ', 'Garden': 'حديقة',
  'Living Room': 'غرفة المعيشة', 'Bedroom': 'غرفة النوم', 'Home Decor': 'ديكور المنزل',
  'Garden & Outdoor': 'الحديقة والأماكن الخارجية', 'Appliances': 'الأجهزة',
  "Men Fashion": 'أزياء رجالية', "Women Fashion": 'أزياء نسائية', 'Watches': 'ساعات',
  'Bags & Accessories': 'حقائب وإكسسوارات', 'Shoes': 'أحذية', 'Jewelry': 'مجوهرات',
  'Full Time': 'دوام كامل', 'Part Time': 'دوام جزئي', 'Freelance': 'عمل حر', 'Internship': 'تدريب',
  'Technology': 'تكنولوجيا', 'Sales & Marketing': 'مبيعات وتسويق', 'Healthcare': 'رعاية صحية',
  'Engineering': 'هندسة', 'Hospitality': 'ضيافة', 'Education': 'تعليم',
  'Baby Gear': 'مستلزمات الأطفال', 'Kids Clothing': 'ملابس أطفال', 'Toys': 'ألعاب', 'Baby Furniture': 'أثاث أطفال',
  'Sports Equipment': 'معدات رياضية', 'Musical Instruments': 'آلات موسيقية', 'Books': 'كتب',
  'Outdoor Gear': 'معدات خارجية', 'Bicycles': 'دراجات',
  'Dogs': 'كلاب', 'Cats': 'قطط', 'Birds': 'طيور', 'Pet Accessories': 'مستلزمات حيوانات', 'Pet Services': 'خدمات حيوانات',
  'Home Services': 'خدمات منزلية', 'Moving & Storage': 'نقل وتخزين', 'Cleaning': 'تنظيف',
  'IT Services': 'خدمات تقنية', 'Events & Photography': 'مناسبات وتصوير', 'Business Services': 'خدمات تجارية',
};

const carBrands = ['Toyota', 'Nissan', 'Honda', 'Mercedes-Benz', 'BMW', 'Audi', 'Lexus', 'Ford', 'Chevrolet', 'Hyundai', 'Kia', 'Mitsubishi', 'Volkswagen', 'Porsche', 'Land Rover', 'Jeep', 'GMC', 'Dodge', 'Infiniti', 'Mazda', 'Suzuki', 'Renault', 'Peugeot', 'Ferrari', 'Lamborghini', 'Bentley', 'Rolls-Royce', 'Maserati', 'Jaguar', 'Other'];
const bodyTypes = ['Sedan', 'SUV', 'Hatchback', 'Coupe', 'Convertible', 'Van', 'Pickup', 'Wagon', 'Other'];
const transmissions = ['Automatic', 'Manual'];
const fuelTypes = ['Petrol', 'Diesel', 'Electric', 'Hybrid'];
const furnishingOptions = ['Furnished', 'Unfurnished', 'Semi-Furnished'];
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

const FieldSelect = ({ label, value, onChange, options, placeholder, testId }) => (
  <div>
    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{label}</label>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 focus:bg-white/8 text-white text-sm transition-all appearance-none"
      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
      data-testid={testId}>
      <option value="" className="bg-[#1A1A2E] text-white/50">{placeholder}</option>
      {options.map(opt => <option key={opt} value={opt} className="bg-[#1A1A2E] text-white">{opt}</option>)}
    </select>
  </div>
);

const FieldInput = ({ label, value, onChange, placeholder, type = 'text', testId }) => (
  <div>
    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{label}</label>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 focus:bg-white/8 text-white placeholder-white/25 text-sm transition-all"
      placeholder={placeholder} data-testid={testId} />
  </div>
);

const CategoryFields = ({ categoryName, metadata, updateMeta, isAr }) => {
  const slug = categoryName?.toLowerCase();

  if (slug === 'motors') {
    return (
      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 space-y-4" data-testid="motors-fields">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
            <Car className="w-4 h-4 text-[#E31C25]" />
          </div>
          <h3 className="font-bold text-white text-sm">{isAr ? 'تفاصيل السيارة' : 'Vehicle Details'}</h3>
        </div>
        <FieldSelect label={isAr ? 'الماركة' : 'Make'} value={metadata.make} onChange={v => updateMeta('make', v)} options={carBrands} placeholder={isAr ? 'اختر الماركة' : 'Select make'} testId="meta-make" />
        <FieldInput label={isAr ? 'الموديل' : 'Model'} value={metadata.model} onChange={v => updateMeta('model', v)} placeholder={isAr ? 'مثال: كامري' : 'e.g. Camry'} testId="meta-model" />
        <FieldSelect label={isAr ? 'السنة' : 'Year'} value={metadata.year} onChange={v => updateMeta('year', v)} options={years.map(String)} placeholder={isAr ? 'اختر السنة' : 'Select year'} testId="meta-year" />
        <FieldInput label={isAr ? 'المسافة المقطوعة (كم)' : 'Mileage (km)'} value={metadata.mileage} onChange={v => updateMeta('mileage', v)} placeholder="0" type="number" testId="meta-mileage" />
        <FieldSelect label={isAr ? 'نوع الهيكل' : 'Body Type'} value={metadata.bodyType} onChange={v => updateMeta('bodyType', v)} options={bodyTypes} placeholder={isAr ? 'اختر النوع' : 'Select type'} testId="meta-body" />
        <FieldSelect label={isAr ? 'ناقل الحركة' : 'Transmission'} value={metadata.transmission} onChange={v => updateMeta('transmission', v)} options={transmissions} placeholder={isAr ? 'اختر' : 'Select'} testId="meta-transmission" />
        <FieldSelect label={isAr ? 'نوع الوقود' : 'Fuel Type'} value={metadata.fuelType} onChange={v => updateMeta('fuelType', v)} options={fuelTypes} placeholder={isAr ? 'اختر' : 'Select'} testId="meta-fuel" />
        <FieldInput label={isAr ? 'اللون' : 'Color'} value={metadata.color} onChange={v => updateMeta('color', v)} placeholder={isAr ? 'مثال: أبيض' : 'e.g. White'} testId="meta-color" />
      </div>
    );
  }

  if (slug === 'property') {
    return (
      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 space-y-4" data-testid="property-fields">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
            <Home className="w-4 h-4 text-[#E31C25]" />
          </div>
          <h3 className="font-bold text-white text-sm">{isAr ? 'تفاصيل العقار' : 'Property Details'}</h3>
        </div>
        <FieldSelect label={isAr ? 'غرف النوم' : 'Bedrooms'} value={metadata.bedrooms} onChange={v => updateMeta('bedrooms', v)} options={['Studio', '1', '2', '3', '4', '5', '6', '7+']} placeholder={isAr ? 'اختر' : 'Select'} testId="meta-bedrooms" />
        <FieldSelect label={isAr ? 'الحمامات' : 'Bathrooms'} value={metadata.bathrooms} onChange={v => updateMeta('bathrooms', v)} options={['1', '2', '3', '4', '5+']} placeholder={isAr ? 'اختر' : 'Select'} testId="meta-bathrooms" />
        <FieldInput label={isAr ? 'المساحة (قدم مربع)' : 'Size (sqft)'} value={metadata.size} onChange={v => updateMeta('size', v)} placeholder="0" type="number" testId="meta-size" />
        <FieldSelect label={isAr ? 'التأثيث' : 'Furnishing'} value={metadata.furnishing} onChange={v => updateMeta('furnishing', v)} options={furnishingOptions} placeholder={isAr ? 'اختر' : 'Select'} testId="meta-furnishing" />
      </div>
    );
  }

  if (slug === 'electronics') {
    return (
      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 space-y-4" data-testid="electronics-fields">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
            <Smartphone className="w-4 h-4 text-[#E31C25]" />
          </div>
          <h3 className="font-bold text-white text-sm">{isAr ? 'تفاصيل المنتج' : 'Product Details'}</h3>
        </div>
        <FieldInput label={isAr ? 'الماركة' : 'Brand'} value={metadata.brand} onChange={v => updateMeta('brand', v)} placeholder={isAr ? 'مثال: Apple' : 'e.g. Apple'} testId="meta-brand" />
        <FieldInput label={isAr ? 'العمر / الاستخدام' : 'Age / Usage'} value={metadata.age} onChange={v => updateMeta('age', v)} placeholder={isAr ? 'مثال: سنة واحدة' : 'e.g. 1 year'} testId="meta-age" />
      </div>
    );
  }

  if (slug === 'jobs') {
    return (
      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 space-y-4" data-testid="jobs-fields">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-[#E31C25]" />
          </div>
          <h3 className="font-bold text-white text-sm">{isAr ? 'تفاصيل الوظيفة' : 'Job Details'}</h3>
        </div>
        <FieldSelect label={isAr ? 'نوع الوظيفة' : 'Job Type'} value={metadata.jobType} onChange={v => updateMeta('jobType', v)} options={['Full Time', 'Part Time', 'Freelance', 'Contract', 'Internship']} placeholder={isAr ? 'اختر' : 'Select'} testId="meta-jobtype" />
        <FieldInput label={isAr ? 'الشركة' : 'Company'} value={metadata.company} onChange={v => updateMeta('company', v)} placeholder={isAr ? 'اسم الشركة' : 'Company name'} testId="meta-company" />
        <FieldSelect label={isAr ? 'مستوى الخبرة' : 'Experience Level'} value={metadata.experience} onChange={v => updateMeta('experience', v)} options={['Entry Level', 'Mid Level', 'Senior', 'Manager', 'Director']} placeholder={isAr ? 'اختر' : 'Select'} testId="meta-experience" />
      </div>
    );
  }

  return null;
};

const PostAdFlow = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [images, setImages] = useState([]);
  const [upgradeOption, setUpgradeOption] = useState(null);

  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCityName, setSelectedCityName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [locations, setLocations] = useState([]);

  const [formData, setFormData] = useState({
    title: '', description: '', price: '', condition: 'used',
    seller_phone: '', seller_whatsapp: '',
  });
  const [metadata, setMetadata] = useState({});

  const isAr = i18n.language === 'ar';
  const tr = (name) => (isAr && categoryTranslations[name]) ? categoryTranslations[name] : name;
  const updateMeta = (key, value) => setMetadata(prev => ({ ...prev, [key]: value }));
  const totalSteps = selectedCategory?.subcategories?.length > 0 ? 4 : 3;

  useEffect(() => {
    trackPageView('post-ad');
    fetchInitialData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const fetchInitialData = async () => {
    try {
      const [categoriesRes, locationsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/locations`),
        axios.get(`${API}/payments/pricing`).catch(() => ({ data: [] })),
      ]);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleBack = () => { if (step > 1) setStep(step - 1); else navigate(-1); };

  const handleCitySelect = (city) => {
    const loc = locations.find(l => l.city === city.en);
    setSelectedCity(loc ? loc.id : city.en);
    setSelectedCityName(city.en);
    setStep(2);
  };

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    if (cat.subcategories?.length > 0) setStep(3);
    else { setSelectedSubcategory(null); setStep(4); }
  };

  const handleSubcategorySelect = (sub) => { setSelectedSubcategory(sub); setStep(4); };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files).slice(0, 10 - images.length);
    setImages([...images, ...files]);
  };

  const removeImage = (index) => setImages(images.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (!formData.title || !formData.price) { alert(t('error.requiredFields')); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/auth'); return; }

      const response = await axios.post(`${API}/listings`, {
        ...formData, price: parseFloat(formData.price),
        category_id: selectedCategory?.id,
        subcategory_id: selectedSubcategory?.id || null,
        location_id: selectedCity, listing_metadata: JSON.stringify(metadata),
      }, { headers: { Authorization: `Bearer ${token}` } });

      const listingId = response.data.id;
      const listingStatus = response.data.status;

      if (images.length > 0) {
        setUploadingImages(true);
        for (let i = 0; i < images.length; i++) {
          const fd = new FormData();
          fd.append('file', images[i]);
          try {
            await axios.post(`${API}/listings/${listingId}/images`, fd, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            setUploadProgress(Math.round(((i + 1) / images.length) * 100));
          } catch (e) { console.error('Image upload error:', e); }
        }
        setUploadingImages(false);
      }

      if (upgradeOption) {
        const paymentRes = await axios.post(`${API}/payments/create-checkout`, {
          listing_id: listingId, payment_type: upgradeOption
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (paymentRes.data.redirect_url) { window.location.href = paymentRes.data.redirect_url; return; }
      }

      if (listingStatus === 'rejected') {
        alert(isAr ? 'تم رفض إعلانك تلقائياً لمخالفته سياسة الاستخدام.' : 'Your ad was automatically rejected for violating our usage policy.');
      } else if (listingStatus === 'pending') {
        alert(isAr ? 'تم إرسال إعلانك للمراجعة وسيتم نشره بعد الموافقة.' : 'Your ad has been submitted for review and will be published after approval.');
      }
      navigate('/');
    } catch (error) {
      console.error('Error creating listing:', error);
      alert(error.response?.data?.detail || 'Error creating listing');
    } finally { setLoading(false); setUploadingImages(false); setUploadProgress(0); }
  };

  // Full-screen dark hero header with step progress
  const StepHeader = ({ title, subtitle, currentStep }) => (
    <div className="hero-gradient relative">
      <div className="noise-overlay" />
      <div className="max-w-2xl mx-auto px-4 pt-4 pb-6 relative z-10">
        <div className="flex items-center justify-between mb-5">
          <button onClick={handleBack} className="w-10 h-10 rounded-xl bg-white/8 backdrop-blur-sm flex items-center justify-center hover:bg-white/15 transition-all active:scale-90" data-testid="post-ad-back">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          {/* Step progress bar */}
          <div className="flex gap-1.5">
            {Array.from({ length: totalSteps + 1 }, (_, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i + 1 <= currentStep ? 'bg-[#E31C25] w-7' : 'bg-white/15 w-4'}`} />
            ))}
          </div>
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-white/8 backdrop-blur-sm flex items-center justify-center hover:bg-white/15 transition-all active:scale-90" data-testid="post-ad-close">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        <div className="animate-fade-up">
          <p className="text-[#E31C25] text-xs font-bold uppercase tracking-widest mb-1.5">
            {isAr ? `الخطوة ${currentStep}` : `Step ${currentStep}`} / {totalSteps + 1}
          </p>
          <h1 className="text-2xl font-extrabold text-white font-heading tracking-tight">{title}</h1>
          {subtitle && <p className="text-white/40 text-sm mt-1">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  // Step 1: City Selection
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460]" data-testid="step-city">
        <StepHeader title={isAr ? 'اختر المدينة' : 'Select your city'} subtitle={isAr ? 'أين تريد نشر إعلانك؟' : 'Where do you want to post?'} currentStep={1} />
        <div className="max-w-2xl mx-auto px-4 pt-2 pb-32 space-y-2">
          {uaeCities.map((city, i) => (
            <button key={city.en} onClick={() => handleCitySelect(city)}
              className={`animate-fade-up stagger-${Math.min(i + 1, 9)} w-full flex items-center justify-between px-4 py-4 bg-white/[0.04] backdrop-blur-sm rounded-2xl border border-white/[0.06] hover:border-[#E31C25]/30 hover:bg-white/[0.08] transition-all group active:scale-[0.98]`}
              data-testid={`city-${city.en.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 bg-[#E31C25]/10 rounded-xl flex items-center justify-center group-hover:bg-[#E31C25] transition-all duration-300">
                  <MapPin className="w-5 h-5 text-[#E31C25] group-hover:text-white transition-colors" strokeWidth={2} />
                </div>
                <span className="text-white/90 font-semibold text-sm group-hover:text-white transition-colors">{isAr ? city.ar : city.en}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#E31C25] group-hover:translate-x-0.5 transition-all" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Step 2: Category Selection
  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460]" data-testid="step-category">
        <StepHeader title={isAr ? 'اختر الفئة' : 'Choose a category'} subtitle={selectedCityName} currentStep={2} />
        <div className="max-w-2xl mx-auto px-4 pt-2 pb-32">
          <div className="grid grid-cols-2 gap-3">
            {categories.map((cat, i) => {
              const IconComp = categoryIcons[cat.name] || Briefcase;
              return (
                <button key={cat.id} onClick={() => handleCategorySelect(cat)}
                  className={`animate-scale-in stagger-${Math.min(i + 1, 9)} relative bg-white/[0.04] backdrop-blur-sm rounded-2xl p-5 border border-white/[0.06] hover:border-[#E31C25]/30 hover:bg-white/[0.08] transition-all group text-left active:scale-[0.97] overflow-hidden`}
                  data-testid={`cat-${cat.slug}`}>
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#E31C25]/[0.04] rounded-full group-hover:bg-[#E31C25]/[0.08] transition-all" />
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-[#E31C25]/10 rounded-2xl flex items-center justify-center mb-3 group-hover:bg-[#E31C25] group-hover:scale-110 transition-all duration-300 shadow-lg shadow-[#E31C25]/5">
                      <IconComp className="w-6 h-6 text-[#E31C25] group-hover:text-white transition-colors" strokeWidth={2} />
                    </div>
                    <span className="font-bold text-white/90 text-sm group-hover:text-white transition-colors">{tr(cat.name)}</span>
                  </div>
                  <ChevronRight className="absolute top-5 right-4 w-4 h-4 text-white/15 group-hover:text-[#E31C25] group-hover:translate-x-0.5 transition-all z-10" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Subcategory Selection
  if (step === 3 && selectedCategory) {
    const CatIcon = categoryIcons[selectedCategory.name] || Briefcase;
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460]" data-testid="step-subcategory">
        <StepHeader title={tr(selectedCategory.name)} subtitle={isAr ? 'اختر الفئة الفرعية' : 'Select a subcategory'} currentStep={3} />
        <div className="max-w-2xl mx-auto px-4 pt-2 pb-32 space-y-2">
          {selectedCategory.subcategories?.map((sub, i) => (
            <button key={sub.id} onClick={() => handleSubcategorySelect(sub)}
              className={`animate-fade-up stagger-${Math.min(i + 1, 9)} w-full flex items-center justify-between px-4 py-4 bg-white/[0.04] backdrop-blur-sm rounded-2xl border border-white/[0.06] hover:border-[#E31C25]/30 hover:bg-white/[0.08] transition-all group active:scale-[0.98]`}
              data-testid={`sub-${sub.slug}`}>
              <div className="flex items-center gap-3.5">
                <div className="w-9 h-9 bg-white/[0.06] rounded-lg flex items-center justify-center group-hover:bg-[#E31C25]/15 transition-all">
                  <CatIcon className="w-4 h-4 text-white/40 group-hover:text-[#E31C25] transition-colors" strokeWidth={2} />
                </div>
                <span className="text-white/80 font-medium text-sm group-hover:text-white transition-colors">{tr(sub.name)}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#E31C25] group-hover:translate-x-0.5 transition-all" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Step 4: Details Form (Full dark theme)
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] pb-40 md:pb-8" data-testid="step-details">
      <StepHeader
        title={isAr ? 'تفاصيل الإعلان' : 'Ad details'}
        subtitle={`${selectedCityName} · ${tr(selectedCategory?.name || '')}${selectedSubcategory ? ' · ' + tr(selectedSubcategory.name) : ''}`}
        currentStep={totalSteps + 1}
      />

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Photos Section */}
        <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 animate-fade-up">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
              <Camera className="w-4 h-4 text-[#E31C25]" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">{isAr ? 'الصور' : 'Photos'}</h3>
              <p className="text-white/30 text-xs">{images.length}/10 {isAr ? 'صور مرفوعة' : 'uploaded'}</p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2.5">
            {images.map((image, index) => (
              <div key={index} className="relative aspect-square bg-white/5 rounded-xl overflow-hidden border border-white/10 group">
                <img src={URL.createObjectURL(image)} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                <button onClick={() => removeImage(index)} className="absolute top-1.5 right-1.5 w-6 h-6 bg-[#E31C25] text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity active:scale-90">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {images.length < 10 && (
              <label className="aspect-square bg-white/[0.04] border-2 border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white/[0.08] hover:border-[#E31C25]/30 transition-all active:scale-95">
                <ImagePlus className="w-6 h-6 text-white/25" />
                <span className="text-[10px] text-white/30 mt-1 font-medium">{isAr ? 'إضافة' : 'Add'}</span>
                <input type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" data-testid="photo-upload" />
              </label>
            )}
          </div>
        </div>

        {/* Title Section */}
        <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 animate-fade-up stagger-1">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-[#E31C25]" />
            </div>
            <h3 className="font-bold text-white text-sm">{t('postAd.adTitle')} <span className="text-[#E31C25]">*</span></h3>
          </div>
          <input type="text" value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 focus:bg-white/8 text-white placeholder-white/25 text-sm transition-all"
            placeholder={t('postAd.titlePlaceholder')} data-testid="post-ad-title" />
        </div>

        {/* Description Section */}
        <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 animate-fade-up stagger-2">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
              <Tag className="w-4 h-4 text-[#E31C25]" />
            </div>
            <h3 className="font-bold text-white text-sm">{t('postAd.description')} <span className="text-[#E31C25]">*</span></h3>
          </div>
          <textarea value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 focus:bg-white/8 text-white placeholder-white/25 text-sm transition-all resize-none"
            placeholder={t('postAd.descPlaceholder')} data-testid="post-ad-description" />
        </div>

        {/* Price & Condition Section */}
        <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 space-y-4 animate-fade-up stagger-3">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-[#E31C25]" />
            </div>
            <h3 className="font-bold text-white text-sm">{isAr ? 'السعر والحالة' : 'Price & Condition'}</h3>
          </div>
          <div>
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{isAr ? 'السعر' : 'Price'} (AED) <span className="text-[#E31C25]">*</span></label>
            <div className="relative">
              <input type="number" value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 ltr:pr-16 rtl:pl-16 py-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 focus:bg-white/8 text-white placeholder-white/25 text-sm transition-all"
                placeholder="0" data-testid="post-ad-price" />
              <span className="absolute ltr:right-4 rtl:left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs font-bold" dir="ltr">AED</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">{t('postAd.condition')}</label>
            <div className="grid grid-cols-2 gap-2.5">
              {['new', 'used'].map((cond) => (
                <button key={cond} onClick={() => setFormData({ ...formData, condition: cond })}
                  className={`px-4 py-3 rounded-xl text-sm font-bold transition-all capitalize active:scale-[0.97] ${
                    formData.condition === cond
                      ? 'bg-[#E31C25] text-white shadow-lg shadow-[#E31C25]/25'
                      : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/8'
                  }`} data-testid={`condition-${cond}`}>
                  {cond === 'new' ? t('listing.new') : t('listing.used')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Category-Specific Fields */}
        {selectedCategory && (
          <div className="animate-fade-up stagger-4">
            <CategoryFields categoryName={selectedCategory.name} metadata={metadata} updateMeta={updateMeta} isAr={isAr} />
          </div>
        )}

        {/* Contact Section */}
        <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 space-y-4 animate-fade-up stagger-5">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
              <Phone className="w-4 h-4 text-[#E31C25]" />
            </div>
            <h3 className="font-bold text-white text-sm">{isAr ? 'معلومات الاتصال' : 'Contact info'}</h3>
          </div>
          <FieldInput label={t('postAd.phone')} value={formData.seller_phone} onChange={v => setFormData({ ...formData, seller_phone: v })} placeholder="+971 XX XXX XXXX" testId="post-ad-phone" />
          <FieldInput label={t('postAd.whatsapp')} value={formData.seller_whatsapp} onChange={v => setFormData({ ...formData, seller_whatsapp: v })} placeholder="+971 XX XXX XXXX" testId="post-ad-whatsapp" />
        </div>

        {/* Upgrade Options */}
        {pricing.length > 0 && (
          <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 space-y-3 animate-fade-up stagger-6">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#E31C25]" />
              </div>
              <h3 className="font-bold text-white text-sm">{t('postAd.boostAd')}</h3>
            </div>
            {pricing.map((plan) => (
              <button key={plan.payment_type} onClick={() => setUpgradeOption(plan.payment_type)}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left active:scale-[0.98] ${
                  upgradeOption === plan.payment_type
                    ? 'border-[#E31C25] bg-[#E31C25]/10'
                    : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
                }`}
                data-testid={`upgrade-${plan.payment_type}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan.payment_type === 'featured' ? 'bg-amber-500/15' : 'bg-blue-500/15'}`}>
                      {plan.payment_type === 'featured' ? <Star className="w-5 h-5 text-amber-400" /> : <Zap className="w-5 h-5 text-blue-400" />}
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm capitalize">{plan.payment_type === 'featured' ? t('postAd.featured') : t('postAd.boosted')}</h4>
                      <p className="text-xs text-white/40">{plan.description}</p>
                    </div>
                  </div>
                  <p className="font-bold text-white text-sm">AED {plan.price}</p>
                </div>
                {upgradeOption === plan.payment_type && (
                  <div className="mt-2 flex items-center gap-2 text-[#E31C25]">
                    <Check className="w-4 h-4" /><span className="text-xs font-semibold">{t('postAd.selected')}</span>
                  </div>
                )}
              </button>
            ))}
            <button onClick={() => setUpgradeOption(null)}
              className={`w-full p-4 rounded-xl border-2 transition-all active:scale-[0.98] ${
                upgradeOption === null
                  ? 'border-[#E31C25] bg-[#E31C25]/10'
                  : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
              }`}
              data-testid="upgrade-none">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-white/80 text-sm">{t('postAd.noThanks')}</span>
                {upgradeOption === null && <Check className="w-5 h-5 text-[#E31C25]" />}
              </div>
            </button>
          </div>
        )}

        {/* Submit Button */}
        <button onClick={handleSubmit} disabled={loading}
          className="w-full py-4 bg-[#E31C25] text-white rounded-2xl font-bold text-base hover:bg-[#C41820] disabled:bg-white/10 disabled:text-white/30 transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98] animate-fade-up stagger-7"
          data-testid="submit-btn">
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{uploadingImages ? `${t('postAd.uploadingPhotos')} ${uploadProgress}%` : t('postAd.posting')}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Upload className="w-5 h-5" />
              <span>{upgradeOption ? t('postAd.payAndPost') : (isAr ? 'نشر الإعلان' : 'Publish Ad')}</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default PostAdFlow;
